/**
 * CopilotHistoryDrawer — collapsible list of audit entries with per-entry
 * Undo. Shown above the chat surface inside CopilotPanel when the user
 * clicks the History icon in the header.
 *
 * Reads via `useCopilotAudit()` (subscribed to localStorage). Undo
 * dispatches through `undoAuditEntry()`, marks the entry as undone, and
 * toasts the result.
 */

import { useMemo } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { CheckCircle2, RotateCcw, XCircle } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { Badge } from "@/lib/ui/badge";
import { cn } from "@/lib/ui/cn";
import {
  markUndone,
  useCopilotAudit,
  clearAudit,
} from "@/lib/copilot/audit";
import { undoAuditEntry, type DispatchStores } from "@/lib/copilot/dispatch";
import { usePlant } from "@/lib/plant/plant-store";
import { useComputedTags } from "@/lib/compute/store";
import { useDashboards } from "@/lib/dashboard/store";
import { useAlarms } from "@/lib/alarms/store";
import { useToast } from "@/lib/ui/toast";

export function CopilotHistoryDrawer({ onClose }: { onClose?: () => void }) {
  void onClose;
  const entries = useCopilotAudit();
  const plant = usePlant();
  const computed = useComputedTags();
  const dashboards = useDashboards();
  const alarms = useAlarms();
  const toast = useToast();

  const stores = useMemo<DispatchStores>(
    () => ({ plant, computed, dashboards, alarms, computedTags: computed }),
    [plant, computed, dashboards, alarms],
  );

  const handleUndo = (entryId: string) => {
    const entry = entries.find((e) => e.id === entryId);
    if (!entry) return;
    const result = undoAuditEntry(entry, stores);
    if (result.ok) {
      markUndone(entryId);
      toast.push("ok", `Undid "${entry.summary}"`);
    } else {
      toast.push("error", result.reason ?? "Undo failed");
    }
  };

  if (entries.length === 0) {
    return (
      <div className="p-4 text-center">
        <p className="text-[11px] text-on-surface-muted">
          No audit history yet. Accept a proposal and it'll appear here with
          one-click undo.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
          History · {entries.length}
        </span>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 text-[10px]"
          onClick={() => {
            if (window.confirm("Clear all audit history? This can't be undone.")) {
              clearAudit();
            }
          }}
        >
          Clear all
        </Button>
      </div>
      <ul className="flex flex-col gap-1.5">
        {entries.map((entry) => {
          const undone = entry.status === "undone";
          return (
            <li
              key={entry.id}
              className={cn(
                "flex items-start gap-2 rounded-md border border-border bg-surface p-2",
                undone && "opacity-60",
              )}
            >
              <div
                className={cn(
                  "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full",
                  undone ? "bg-surface-muted text-on-surface-muted" : "bg-success/15 text-success",
                )}
              >
                {undone ? <XCircle className="size-3" /> : <CheckCircle2 className="size-3" />}
              </div>
              <div className="flex flex-1 flex-col gap-0.5">
                <span className="text-[11px] font-medium text-on-surface">
                  {entry.summary}
                </span>
                <div className="flex items-center gap-1.5 text-[9px] text-on-surface-muted">
                  <Badge variant="secondary" className="font-mono text-[8px]">
                    {entry.toolId}
                  </Badge>
                  <time
                    dateTime={entry.t}
                    title={format(new Date(entry.t), "PPpp")}
                  >
                    {formatRelative(entry.t)}
                  </time>
                </div>
              </div>
              {!undone && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-1.5 text-[10px]"
                  onClick={() => handleUndo(entry.id)}
                  title="Undo"
                >
                  <RotateCcw className="size-3" />
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function formatRelative(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}
